import React, { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../api/client";

// ─── constants ────────────────────────────────────────────────────────────────

const CLASSIFICATIONS = ["public", "internal", "confidential", "restricted", "executive"];
const SOURCE_TYPES = ["pdf", "docx", "xlsx", "pptx", "txt", "csv"];

function hasPerm(user, perm) {
  return (user?.permissions || []).includes(perm);
}

// ─── shared UI ────────────────────────────────────────────────────────────────

function ClassBadge({ cls }) {
  const map = {
    public: "bg-blue-50 text-blue-600 border-blue-100",
    internal: "bg-indigo-50 text-indigo-600 border-indigo-100",
    confidential: "bg-yellow-50 text-yellow-700 border-yellow-100",
    restricted: "bg-orange-50 text-orange-700 border-orange-100",
    executive: "bg-red-50 text-red-700 border-red-100",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-wide ${map[cls] || "bg-gray-100 text-gray-500 border-gray-200"}`}>
      {cls}
    </span>
  );
}

function StatusDot({ published }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${published ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${published ? "bg-green-500" : "bg-gray-400"}`} />
      {published ? "Aktif" : "Pending"}
    </span>
  );
}

function Toast({ message, type = "success", onDismiss }) {
  const colors = type === "error"
    ? "bg-red-50 border-red-200 text-red-700"
    : "bg-green-50 border-green-200 text-green-700";
  return (
    <div className={`fixed bottom-5 right-5 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg z-[60] max-w-[340px] ${colors}`}>
      <span className="material-symbols-outlined text-[18px] flex-shrink-0">{type === "error" ? "error" : "check_circle"}</span>
      <p className="text-sm font-medium flex-1">{message}</p>
      <button onClick={onDismiss} className="opacity-60 hover:opacity-100">
        <span className="material-symbols-outlined text-[16px]">close</span>
      </button>
    </div>
  );
}

function Modal({ title, onClose, children, maxWidth = "max-w-lg" }) {
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidth} my-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:border-[#10a37f] focus:ring-2 focus:ring-[#10a37f]/10 outline-none transition-all bg-white disabled:bg-gray-50";
const labelCls = "block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide";

// ─── Edit Document Modal ──────────────────────────────────────────────────────

function EditDocModal({ doc, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: doc.title || "",
    classification_level: doc.classification_level || "internal",
    is_published: Boolean(doc.is_published),
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (file) {
        const extension = file.name.split(".").pop()?.toLowerCase() || doc.source_type || "manual";
        const data = new FormData();
        data.append("title", form.title);
        data.append("classification_level", form.classification_level);
        data.append("is_published", String(form.is_published));
        data.append("source_type", SOURCE_TYPES.includes(extension) ? extension : "manual");
        data.append("file", file);
        await api.updateDocumentWithFile(doc.id, data);
        onSaved("Dokumen dan file berhasil diupdate. File lama sudah dihapus.");
      } else {
        await api.updateDocument(doc.id, form);
        onSaved("Dokumen berhasil diupdate. File lama tetap digunakan.");
      }
    } catch (err) {
      setError(err.message || "Gagal update dokumen");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title="Edit Dokumen" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
        <div>
          <label className={labelCls}>Judul</label>
          <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div>
          <label className={labelCls}>Classification</label>
          <select className={inputCls} value={form.classification_level} onChange={(e) => setForm({ ...form, classification_level: e.target.value })}>
            {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="w-4 h-4 rounded accent-[#10a37f]" />
          <span className="text-sm font-medium text-gray-700">Aktifkan (Published)</span>
        </label>
        <div>
          <label className={labelCls}>Ganti File (Opsional)</label>
          <input
            ref={fileRef}
            type="file"
            className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#10a37f]/10 file:text-[#10a37f] hover:file:bg-[#10a37f]/20 transition-colors"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <p className="mt-2 text-xs text-gray-400">
            Kosongkan jika hanya edit metadata. Kalau pilih file baru, file lama akan dihapus dan dokumen masuk antrean reingest.
          </p>
          {file && (
            <button
              type="button"
              onClick={() => {
                setFile(null);
                if (fileRef.current) fileRef.current.value = "";
              }}
              className="mt-2 text-xs font-semibold text-gray-500 hover:text-red-600"
            >
              Batal ganti file
            </button>
          )}
        </div>
        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Batal</button>
          <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl bg-[#202123] hover:bg-[#10a37f] text-white text-sm font-semibold transition-colors disabled:opacity-50">
            {loading ? "Menyimpan..." : file ? "Simpan & Ganti File" : "Simpan"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Edit Collection Modal ────────────────────────────────────────────────────

function EditCollectionModal({ col, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: col.name || "",
    description: col.description || "",
    default_classification: col.default_classification || "internal",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.updateCollection(col.id, form);
      onSaved("Collection berhasil diupdate.");
    } catch (err) {
      setError(err.message || "Gagal update collection");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title={`Edit Collection — ${col.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
        <div>
          <label className={labelCls}>Nama</label>
          <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div>
          <label className={labelCls}>Deskripsi</label>
          <textarea className={inputCls} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="opsional" />
        </div>
        <div>
          <label className={labelCls}>Default Classification</label>
          <select className={inputCls} value={form.default_classification} onChange={(e) => setForm({ ...form, default_classification: e.target.value })}>
            {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Batal</button>
          <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl bg-[#202123] hover:bg-[#10a37f] text-white text-sm font-semibold transition-colors disabled:opacity-50">
            {loading ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Upload Document Form ─────────────────────────────────────────────────────

function UploadForm({ collections, selectedCollection, canCreate, onUploaded }) {
  const [form, setForm] = useState({
    collection_id: selectedCollection?.id || "",
    title: "",
    classification: selectedCollection?.default_classification || "internal",
    source_type: "pdf",
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!selectedCollection?.id) return;
    setForm((current) => ({
      ...current,
      collection_id: selectedCollection.id,
      classification: current.classification || selectedCollection.default_classification || "internal",
    }));
  }, [selectedCollection]);

  async function upload(e) {
    e.preventDefault();
    if (!file || !form.collection_id) return;
    setUploading(true);
    setError("");
    try {
      const data = new FormData();
      data.append("file", file);
      data.append("collection_id", form.collection_id);
      data.append("title", form.title || file.name);
      data.append("classification", form.classification);
      data.append("source_type", form.source_type);
      const res = await api.uploadDocument(data);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setForm((f) => ({ ...f, title: "" }));
      onUploaded(res.message || "Dokumen berhasil diupload.");
    } catch (err) {
      setError(err.message || "Gagal upload");
    } finally {
      setUploading(false);
    }
  }

  return (
    <form onSubmit={upload} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
      <h2 className="font-bold text-gray-900">Upload Dokumen</h2>
      {!canCreate && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl p-3 text-sm">
          <span className="material-symbols-outlined text-[16px]">warning</span>
          Akun ini tidak punya izin upload dokumen.
        </div>
      )}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}

      <div>
        <label className={labelCls}>Collection *</label>
        <select
          className={inputCls}
          value={form.collection_id}
          onChange={(e) => setForm({ ...form, collection_id: e.target.value })}
          required
          disabled={!canCreate || Boolean(selectedCollection?.id)}
        >
          <option value="">Pilih collection...</option>
          {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {selectedCollection?.id && form.collection_id === selectedCollection.id && (
          <p className="mt-2 text-xs text-[#10a37f] font-medium">
            Upload akan masuk ke collection yang sedang dipilih: {selectedCollection.name}.
          </p>
        )}
      </div>
      <div>
        <label className={labelCls}>Judul</label>
        <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Otomatis dari nama file" disabled={!canCreate} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Classification</label>
          <select className={inputCls} value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })} disabled={!canCreate}>
            {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Tipe</label>
          <select className={inputCls} value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })} disabled={!canCreate}>
            {SOURCE_TYPES.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls}>File *</label>
        <input
          ref={fileRef}
          type="file"
          className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#10a37f]/10 file:text-[#10a37f] hover:file:bg-[#10a37f]/20 transition-colors disabled:opacity-50"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          disabled={!canCreate}
        />
      </div>
      <button
        type="submit"
        disabled={!canCreate || uploading || !file || !form.collection_id}
        className="w-full py-2.5 rounded-xl bg-[#202123] hover:bg-[#10a37f] text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <span className="material-symbols-outlined text-[18px]">{uploading ? "hourglass_empty" : "upload"}</span>
        {uploading ? "Mengupload..." : "Upload Dokumen"}
      </button>
    </form>
  );
}

// ─── Create Collection Form ───────────────────────────────────────────────────

function CreateCollectionForm({ canCreate, onCreated }) {
  const [form, setForm] = useState({ code: "", name: "", default_classification: "internal" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.createCollection(form);
      setForm({ code: "", name: "", default_classification: "internal" });
      onCreated("Collection berhasil dibuat.");
    } catch (err) {
      setError(err.message || "Gagal membuat collection");
    } finally {
      setLoading(false);
    }
  }

  if (!canCreate) return null;

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
      <h2 className="font-bold text-gray-900">Collection Baru</h2>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
      <div>
        <label className={labelCls}>Kode *</label>
        <input className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, "_") })} placeholder="KODE_COLLECTION" required />
      </div>
      <div>
        <label className={labelCls}>Nama *</label>
        <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama collection..." required />
      </div>
      <div>
        <label className={labelCls}>Default Classification</label>
        <select className={inputCls} value={form.default_classification} onChange={(e) => setForm({ ...form, default_classification: e.target.value })}>
          {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <button type="submit" disabled={loading} className="w-full py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
        <span className="material-symbols-outlined text-[18px]">create_new_folder</span>
        {loading ? "Membuat..." : "Buat Collection"}
      </button>
    </form>
  );
}

// ─── Documents Table ──────────────────────────────────────────────────────────

function DocumentsTable({ collectionId, canUpdate, canDelete, onMsg }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDoc, setEditDoc] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [reingesting, setReingesting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.collectionDocuments(collectionId);
      setDocs(Array.isArray(rows) ? rows : []);
    } catch (err) {
      onMsg("Gagal memuat dokumen: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [collectionId, onMsg]);

  useEffect(() => { load(); }, [load]);

  async function archive(docId) {
    if (!window.confirm("Arsipkan dokumen ini? Dokumen tidak bisa diakses setelah diarsipkan.")) return;
    setArchiving(docId);
    try {
      await api.archiveDocument(docId);
      await load();
      onMsg("Dokumen berhasil diarsipkan.");
    } catch (err) {
      onMsg("Gagal arsipkan: " + err.message, "error");
    } finally {
      setArchiving(null);
    }
  }

  async function reingest(docId) {
    setReingesting(docId);
    try {
      await api.reingestDocument(docId);
      await load();
      onMsg("Reingest berhasil dijadwalkan.");
    } catch (err) {
      onMsg("Gagal reingest: " + err.message, "error");
    } finally {
      setReingesting(null);
    }
  }

  if (loading) return (
    <div className="py-8 text-center text-gray-400 text-sm">
      <span className="material-symbols-outlined text-2xl text-gray-200 block mb-1 animate-spin">refresh</span>
      Memuat dokumen...
    </div>
  );

  if (docs.length === 0) return (
    <div className="py-10 text-center">
      <span className="material-symbols-outlined text-3xl text-gray-200 block mb-2">description</span>
      <p className="text-sm text-gray-400">Belum ada dokumen di collection ini.</p>
    </div>
  );

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Dokumen</th>
              <th className="text-left px-3 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Tipe</th>
              <th className="text-left px-3 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Class</th>
              <th className="text-left px-3 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Status</th>
              {(canUpdate || canDelete) && (
                <th className="text-right px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Aksi</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {docs.map((doc) => (
              <tr key={doc.id} className="hover:bg-gray-50/60 transition-colors">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900 text-sm truncate max-w-[200px]">{doc.title}</p>
                  <p className="text-[11px] text-gray-400 truncate max-w-[200px]">{doc.source_name || "-"}</p>
                </td>
                <td className="px-3 py-3">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[11px] font-semibold uppercase">{doc.source_type || "-"}</span>
                </td>
                <td className="px-3 py-3"><ClassBadge cls={doc.classification_level} /></td>
                <td className="px-3 py-3"><StatusDot published={doc.is_published} /></td>
                {(canUpdate || canDelete) && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {canUpdate && (
                        <>
                          <button
                            onClick={() => setEditDoc(doc)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#202123] hover:bg-gray-100 transition-colors"
                            title="Edit"
                          >
                            <span className="material-symbols-outlined text-[16px]">edit</span>
                          </button>
                          <button
                            onClick={() => reingest(doc.id)}
                            disabled={reingesting === doc.id}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-40"
                            title="Reingest"
                          >
                            <span className={`material-symbols-outlined text-[16px] ${reingesting === doc.id ? "animate-spin" : ""}`}>refresh</span>
                          </button>
                        </>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => archive(doc.id)}
                          disabled={archiving === doc.id}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                          title="Arsipkan"
                        >
                          <span className={`material-symbols-outlined text-[16px] ${archiving === doc.id ? "animate-pulse" : ""}`}>archive</span>
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editDoc && (
        <EditDocModal
          doc={editDoc}
          onClose={() => setEditDoc(null)}
          onSaved={(msg) => { setEditDoc(null); load(); onMsg(msg); }}
        />
      )}
    </>
  );
}

// ─── main view ────────────────────────────────────────────────────────────────

export default function KnowledgeView() {
  const { collections, loadCollections, user } = useOutletContext();

  const canCreate = hasPerm(user, "knowledge.create");
  const canUpdate = hasPerm(user, "knowledge.update");
  const canDelete = hasPerm(user, "knowledge.delete");
  const canManageAccess = hasPerm(user, "knowledge.manage_access");

  const [selectedCol, setSelectedCol] = useState(null);
  const [editCollection, setEditCollection] = useState(null);
  const [tab, setTab] = useState("docs"); // docs | upload | settings
  const [toast, setToast] = useState(null);
  const [docsKey, setDocsKey] = useState(0); // force re-mount DocumentsTable

  function showToast(msg, type = "success") {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 4500);
  }

  function handleCollectionAction(msg) {
    loadCollections();
    showToast(msg);
  }

  function handleDocAction(msg) {
    setDocsKey((k) => k + 1);
    showToast(msg);
  }

  function selectCol(col) {
    setSelectedCol(col);
    setTab("docs");
  }

  return (
    <main className="flex-1 flex flex-col min-h-0 bg-gray-50 overflow-y-auto">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="flex items-center h-14 pl-14 pr-4 lg:px-6 gap-3">
          <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
            <span className="material-symbols-outlined text-white text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>library_books</span>
          </div>
          <div className="min-w-0">
            <h1 className="font-bold text-gray-900 text-base leading-tight">Knowledge Base</h1>
            <p className="text-[11px] text-gray-400 hidden sm:block">Kelola dokumen dan koleksi pengetahuan</p>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* ── Left panel: Collections ── */}
        <aside className="flex-shrink-0 lg:w-[280px] bg-white border-b lg:border-b-0 lg:border-r border-gray-100 flex flex-col max-h-[45vh] lg:max-h-none">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Collections</span>
            <span className="text-xs text-gray-400">{collections.length}</span>
          </div>

          {/* Collection list */}
          <div className="flex-1 overflow-y-auto">
            {collections.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">
                <span className="material-symbols-outlined text-2xl text-gray-200 block mb-1">folder_open</span>
                Belum ada collection.
              </div>
            ) : (
              <div className="py-1">
                {collections.map((col) => (
                  <div
                    key={col.id}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors group ${
                      selectedCol?.id === col.id
                        ? "bg-[#10a37f]/5 border-l-2 border-[#10a37f]"
                        : "hover:bg-gray-50 border-l-2 border-transparent"
                    }`}
                    onClick={() => selectCol(col)}
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#10a37f]/10 flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-[#10a37f] text-[16px]" style={{ fontVariationSettings: "'FILL' 1" }}>folder</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{col.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-gray-400">{col.code}</span>
                        <ClassBadge cls={col.default_classification} />
                      </div>
                    </div>
                    {canUpdate && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditCollection(col); }}
                        className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all flex-shrink-0"
                        title="Edit collection"
                      >
                        <span className="material-symbols-outlined text-[14px]">edit</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create collection at bottom of left panel */}
          {canCreate && (
            <div className="border-t border-gray-100 p-4">
              <CreateCollectionForm canCreate={canCreate} onCreated={handleCollectionAction} />
            </div>
          )}
        </aside>

        {/* ── Right panel: Content ── */}
        <div className="flex-1 overflow-y-auto">
          {!selectedCol ? (
            <div className="flex flex-col items-center justify-center h-full py-16 text-center px-6">
              <span className="material-symbols-outlined text-5xl text-gray-200 mb-3">folder_open</span>
              <p className="font-semibold text-gray-500">Pilih collection di sebelah kiri</p>
              <p className="text-sm text-gray-400 mt-1">untuk melihat dan mengelola dokumen</p>
            </div>
          ) : (
            <div className="p-4 md:p-6">
              {/* Collection header */}
              <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-gray-900">{selectedCol.name}</h2>
                    <ClassBadge cls={selectedCol.default_classification} />
                  </div>
                  <p className="text-sm text-gray-400 mt-1">{selectedCol.code}</p>
                </div>
                <div className="flex gap-2">
                  {canUpdate && (
                    <button
                      onClick={() => setEditCollection(selectedCol)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[14px]">edit</span>
                      Edit Collection
                    </button>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-100 mb-5 overflow-x-auto">
                {[
                  { id: "docs", label: "Dokumen", icon: "description" },
                  ...(canCreate ? [{ id: "upload", label: "Upload", icon: "upload" }] : []),
                  ...(canManageAccess ? [{ id: "access", label: "Akses", icon: "key" }] : []),
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
                      tab === t.id
                        ? "border-[#10a37f] text-[#10a37f]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="material-symbols-outlined text-[16px]">{t.icon}</span>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              {tab === "docs" && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Daftar Dokumen</span>
                    {canCreate && (
                      <button onClick={() => setTab("upload")} className="text-xs text-[#10a37f] font-semibold hover:underline flex items-center gap-1">
                        <span className="material-symbols-outlined text-[14px]">add</span>Upload baru
                      </button>
                    )}
                  </div>
                  <DocumentsTable
                    key={`${selectedCol.id}-${docsKey}`}
                    collectionId={selectedCol.id}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                    onMsg={(msg, type = "success") => showToast(msg, type)}
                  />
                </div>
              )}

              {tab === "upload" && (
                <UploadForm
                  collections={collections}
                  selectedCollection={selectedCol}
                  canCreate={canCreate}
                  onUploaded={(msg) => { setTab("docs"); handleDocAction(msg); }}
                />
              )}

              {tab === "access" && canManageAccess && (
                <AccessRulesForm collection={selectedCol} onMsg={showToast} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {editCollection && (
        <EditCollectionModal
          col={editCollection}
          onClose={() => setEditCollection(null)}
          onSaved={(msg) => { setEditCollection(null); loadCollections(); showToast(msg); }}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </main>
  );
}

// ─── Access Rules Form ────────────────────────────────────────────────────────

function AccessRulesForm({ collection, onMsg }) {
  const [form, setForm] = useState({ principal_type: "user", principal_id: "" });
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  const principalLabels = {
    user: "User",
    role: "Role",
    department: "Department",
  };

  const loadOptions = useCallback(async () => {
    setSearching(true);
    try {
      const rows = await api.searchKnowledgePrincipals(form.principal_type, query);
      setOptions(Array.isArray(rows) ? rows : []);
    } catch (err) {
      onMsg("Gagal mencari principal: " + err.message, "error");
    } finally {
      setSearching(false);
    }
  }, [form.principal_type, query, onMsg]);

  useEffect(() => {
    const timer = setTimeout(loadOptions, 250);
    return () => clearTimeout(timer);
  }, [loadOptions]);

  async function submit(e) {
    e.preventDefault();
    if (!form.principal_id) return;
    setLoading(true);
    try {
      await api.createAccessRule({
        collection_id: collection.id,
        document_id: null,
        principal_type: form.principal_type,
        principal_id: form.principal_id.trim(),
        access_level: "read",
        can_read: true,
        can_upload: false,
        can_manage: false,
        is_active: true,
        expires_at: null,
      });
      setForm((f) => ({ ...f, principal_id: "" }));
      setSelected(null);
      setQuery("");
      onMsg(`Akses berhasil diberikan ke ${selected?.name || "principal"}.`);
    } catch (err) {
      onMsg("Gagal: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
      <h2 className="font-bold text-gray-900">Berikan Akses ke Collection</h2>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-3">
          <div>
            <label className={labelCls}>Tipe Principal</label>
            <select
              className={inputCls}
              value={form.principal_type}
              onChange={(e) => {
                setForm({ principal_type: e.target.value, principal_id: "" });
                setSelected(null);
                setQuery("");
                setOptions([]);
              }}
            >
              <option value="user">User</option>
              <option value="role">Role</option>
              <option value="department">Department</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Cari {principalLabels[form.principal_type]}</label>
            <input
              className={inputCls}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setForm((current) => ({ ...current, principal_id: "" }));
                setSelected(null);
              }}
              placeholder={`Ketik nama ${principalLabels[form.principal_type].toLowerCase()}...`}
            />
          </div>
        </div>

        <div className="border border-gray-100 rounded-xl overflow-hidden bg-gray-50">
          <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center justify-between">
            <span>Hasil pencarian</span>
            {searching && <span>Mencari...</span>}
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 bg-white">
            {options.map((item) => {
              const active = form.principal_id === item.id;
              return (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => {
                    setSelected(item);
                    setForm((current) => ({ ...current, principal_id: item.id }));
                    setQuery(item.name);
                  }}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-3 text-left hover:bg-[#10a37f]/5 transition-colors ${active ? "bg-[#10a37f]/10" : ""}`}
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-gray-900 truncate">{item.name}</p>
                    <p className="text-xs text-gray-400 truncate">{item.subtitle || item.id}</p>
                  </div>
                  {active && <span className="material-symbols-outlined text-[#10a37f] text-[18px]">check_circle</span>}
                </button>
              );
            })}
            {!options.length && !searching && (
              <div className="px-3 py-6 text-center text-sm text-gray-400">
                Tidak ada {principalLabels[form.principal_type].toLowerCase()} ditemukan.
              </div>
            )}
          </div>
        </div>

        {selected && (
          <div className="bg-[#10a37f]/10 border border-[#10a37f]/20 text-[#08745d] rounded-xl p-3 text-sm">
            Akses akan diberikan ke <strong>{selected.name}</strong>
            {selected.subtitle ? <span> ({selected.subtitle})</span> : null}.
          </div>
        )}

        <button type="submit" disabled={loading || !form.principal_id} className="w-full py-2.5 rounded-xl bg-[#10a37f] hover:bg-[#0e8f6c] text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          <span className="material-symbols-outlined text-[18px]">key</span>
          {loading ? "Menyimpan..." : "Berikan Akses"}
        </button>
      </form>
    </div>
  );
}
